import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, X, Sparkles, CreditCard, Zap } from 'lucide-react';

const STEPS = [
  {
    target: 'create-event-button',
    icon: Sparkles,
    title: 'Create your first event',
    copy: 'Start here. Create your first event in minutes.',
    position: 'below-left',
  },
  {
    target: 'setup-defaults-cta',
    fallbackTarget: 'empty-state',
    icon: Zap,
    title: 'Save your defaults',
    copy: 'Use your saved defaults to skip setup next time.',
    position: 'above',
  },
  {
    target: 'payment-methods-button',
    icon: CreditCard,
    title: 'Add a payment method',
    copy: 'Add a payment method so guests can actually pay you.',
    position: 'below-left',
  },
];

function getRect(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  return { rect: el.getBoundingClientRect(), el };
}

export default function QuickStartOverlay({ onDismiss }) {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const measure = useCallback(() => {
    const s = STEPS[step];
    const result = getRect(s.target) || (s.fallbackTarget ? getRect(s.fallbackTarget) : null);
    setTargetRect(result?.rect || null);
  }, [step]);

  useEffect(() => {
    // Fade in
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => {
      localStorage.setItem('vg_quickstart_seen', 'true');
      onDismiss();
    }, 200);
  };

  const next = () => {
    if (step >= STEPS.length - 1) {
      dismiss();
    } else {
      setStep(step + 1);
    }
  };

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  // Tooltip position relative to target
  const getTooltipStyle = () => {
    if (!targetRect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const pos = current.position;
    const pad = 12;
    const vw = window.innerWidth;

    if (pos === 'below-left') {
      let left = Math.min(targetRect.right, vw - 320);
      left = Math.max(16, left - 300);
      return { top: `${targetRect.bottom + pad}px`, left: `${left}px` };
    }
    if (pos === 'above') {
      const centerX = targetRect.left + targetRect.width / 2;
      let left = Math.max(16, centerX - 150);
      left = Math.min(left, vw - 320);
      return { bottom: `${window.innerHeight - targetRect.top + pad}px`, left: `${left}px` };
    }
    // default: below center
    const centerX = targetRect.left + targetRect.width / 2;
    let left = Math.max(16, centerX - 150);
    left = Math.min(left, vw - 320);
    return { top: `${targetRect.bottom + pad}px`, left: `${left}px` };
  };

  // Spotlight cutout
  const getSpotlightStyle = () => {
    if (!targetRect) return { display: 'none' };
    const inset = 6;
    return {
      top: `${targetRect.top - inset}px`,
      left: `${targetRect.left - inset}px`,
      width: `${targetRect.width + inset * 2}px`,
      height: `${targetRect.height + inset * 2}px`,
    };
  };

  return (
    <div
      className={`fixed inset-0 z-[60] transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      data-testid="quickstart-overlay"
    >
      {/* Dim backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={dismiss} />

      {/* Spotlight ring around target */}
      {targetRect && (
        <div
          className="absolute rounded-sm ring-2 ring-[#C6A66B]/60 pointer-events-none"
          style={getSpotlightStyle()}
          data-testid="quickstart-spotlight"
        >
          <div className="absolute inset-0 rounded-sm bg-[#C6A66B]/5 animate-pulse" />
        </div>
      )}

      {/* Tooltip card */}
      <div
        className="absolute w-[300px] max-w-[calc(100vw-32px)] z-[61]"
        style={getTooltipStyle()}
        data-testid="quickstart-tooltip"
      >
        <div className="bg-[#0D0D0D] border border-[#C6A66B]/30 rounded-sm shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
          {/* Gold accent top */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#C6A66B] to-transparent" />

          <div className="p-4">
            {/* Header row */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#C6A66B]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-[#C6A66B]" />
                </div>
                <span className="text-xs font-semibold text-[#C6A66B] uppercase tracking-wider">
                  Step {step + 1} of {STEPS.length}
                </span>
              </div>
              <button
                onClick={dismiss}
                className="text-[#555] hover:text-[#B9B1A6] transition-colors p-0.5"
                data-testid="quickstart-skip"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <h3 className="text-sm font-bold text-[#F3EEE7] mb-1">{current.title}</h3>
            <p className="text-xs text-[#B9B1A6] leading-relaxed mb-4">{current.copy}</p>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <button
                onClick={dismiss}
                className="text-xs text-[#555] hover:text-[#B9B1A6] transition-colors"
                data-testid="quickstart-dismiss"
              >
                Skip tour
              </button>
              <button
                onClick={next}
                className="flex items-center gap-1.5 bg-[#C6A66B] hover:bg-[#D4AF37] text-[#050505] px-4 py-2 rounded-sm text-xs font-bold transition-colors"
                data-testid="quickstart-next"
              >
                {isLast ? 'Got it' : 'Next'}
                {!isLast && <ArrowRight className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
